/**
 * API Client for Entity Comments (Issue-like discussions)
 *
 * Comments are NOT the same as notes/descriptions.
 * They represent a chronological feed of messages attached to an entity.
 */

import { httpClient } from './client';
import {
  assertObject,
  assertEntityId,
  assertString,
  assertEnum,
  assertArrayOf,
} from '../types/validation';

export type TEntityType = "source" | "schema" | "table" | "column" | "provider";

export interface IEntityComment {
  readonly id: string; // UUID
  readonly entityType: TEntityType;
  readonly entityId: string;
  readonly body: string; // HTML (TipTap)
  readonly createdAt: string;
  readonly createdBy: string;
}

/**
 * Запрос на создание комментария.
 *
 * Поля автора здесь НЕТ (comments-R07): автор — пользователь запроса, и извне
 * он не принимается ни на одном канале. Его передача — отказ валидации на
 * стороне продукта, а не молчаливое игнорирование.
 */
export interface ICreateEntityCommentRequest {
  readonly entityType: TEntityType;
  readonly entityId: string;
  readonly body: string;
}

/**
 * Идентификатор сущности комментария у схемы, таблицы и колонки
 * (comments-R12): числовой идентификатор источника, точка и имя-путь объекта —
 * «источник.схема», «источник.схема.таблица», «источник.схема.таблица.колонка».
 *
 * Имя-путь собирается из имён как есть: продукт сравнивает его с именами
 * объектов источника целиком, по точке не деля (comments-R13). Без источника
 * или без имени объекта идентификатора нет — пустая строка, и лента по ней не
 * запрашивается.
 */
export function sourceScopedCommentEntityId(
  sourceId: string,
  ...names: readonly string[]
): string {
  if (sourceId.trim().length === 0 || names.length === 0) return "";
  if (names.some((name) => name.length === 0)) return "";
  return [sourceId, ...names].join(".");
}

// ============================================================================
// Runtime Validators
// ============================================================================

const ENTITY_TYPES: readonly TEntityType[] = ["source", "schema", "table", "column", "provider"];

function validateEntityComment(data: unknown, path: string): IEntityComment {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    entityType: assertEnum(obj['entityType'], ENTITY_TYPES, `${path}.entityType`),
    // Ключ адресации по виду сущности: идентификатор источника или группы
    // либо «источник.схема[.таблица[.колонка]]» (comments-R12). Продукт
    // возвращает его ровно тем, чем получил, поэтому числовой формы у него нет.
    entityId: assertString(obj['entityId'], `${path}.entityId`),
    body: assertString(obj['body'], `${path}.body`),
    createdAt: assertString(obj['createdAt'], `${path}.createdAt`),
    createdBy: assertString(obj['createdBy'], `${path}.createdBy`),
  };
}

// ============================================================================
// API Client Interface
// ============================================================================

export interface ICommentsApiClient {
  listEntityComments(
    projectId: string,
    entityType: TEntityType,
    entityId: string,
  ): Promise<readonly IEntityComment[]>;

  createEntityComment(
    projectId: string,
    request: ICreateEntityCommentRequest,
  ): Promise<IEntityComment>;
}

class CommentsApiClient implements ICommentsApiClient {
  async listEntityComments(
    projectId: string,
    entityType: TEntityType,
    entityId: string,
  ): Promise<readonly IEntityComment[]> {
    const response = await httpClient.get<unknown>(
      `/projects/${projectId}/comments`,
      { params: { entityType, entityId } }
    );
    return assertArrayOf(response.data, validateEntityComment, 'listEntityComments');
  }

  async createEntityComment(
    projectId: string,
    request: ICreateEntityCommentRequest,
  ): Promise<IEntityComment> {
    // Отказ продукта пробрасывается КАК ЕСТЬ: признак причины и параметры —
    // самостоятельные части отказа, и подменять их обобщённым текстом
    // «не удалось создать комментарий» нельзя (api-errors-R06–R08). Именно эта
    // подмена не давала показать причину рядом с полем ввода (comments-R11).
    const response = await httpClient.post<unknown>(
      `/projects/${projectId}/comments`,
      request
    );
    return validateEntityComment(response.data, 'createEntityComment');
  }
}

// Singleton instance
let commentsApiClient: ICommentsApiClient | null = null;

export function getCommentsApiClient(): ICommentsApiClient {
  commentsApiClient ??= new CommentsApiClient();
  return commentsApiClient;
}

